import React, { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { flushSync } from 'react-dom'
import { omitBy } from 'lodash-es'
import { B2BGetOrdersReq, TabType, OutboundWaitingUrlParams, isB2BParams, B2BUrlParams } from '@/services/outbound/outboundWaitingStatus/types'
import { useB2BOutboundData } from '../hooks/useOutboundWaitingData'
import B2BContent from './B2BContent'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useShipperFieldSelection } from '@/components/system/ShipperSelector'
import { SHIPPER_TYPES } from '@/services/shipper/apis'

interface B2BContentWrapperProps {
  parsedQs: Partial<OutboundWaitingUrlParams & { isShipperNamesSelectedAll?: boolean }>
  filteredWarehouses?: string[]
  replaceQuery: (query: any) => void
  reload: () => void
  currentTabType: TabType
}

export type B2BOutboundForm = B2BGetOrdersReq & {
  logisticsServiceTypes: string[]
}

// B2B 탭에서 사용되는 주문유형들

export const getB2BInitForm = (parsedQs: Partial<B2BUrlParams> = {}): B2BOutboundForm => {
  return {
    shipperNames: [],
    logisticsServiceType: parsedQs.logisticsServiceTypes?.[0] ?? '',
    logisticsServiceTypes: parsedQs.logisticsServiceTypes ? [parsedQs.logisticsServiceTypes] : [],
    outboundDocumentNumbers: parsedQs.outboundDocumentNumbers
      ? Array.isArray(parsedQs.outboundDocumentNumbers)
        ? parsedQs.outboundDocumentNumbers
        : [parsedQs.outboundDocumentNumbers]
      : [],
    plannedCutoffDateFrom: parsedQs.plannedCutoffDateFrom,
    plannedCutoffDateTo: parsedQs.plannedCutoffDateTo,
    orderType: parsedQs.orderType,
  }
}

function B2BContentWrapper({ parsedQs, replaceQuery, reload, currentTabType, filteredWarehouses }: B2BContentWrapperProps) {
  const b2bParams = isB2BParams(parsedQs) ? parsedQs : { tabType: 'B2B' as const }

  const form = useForm<B2BOutboundForm>({
    defaultValues: getB2BInitForm(b2bParams),
  })

  const [queryKey, setQueryKey] = useState<B2BGetOrdersReq & { logisticsServiceTypes: string[] }>(getB2BInitForm(b2bParams))

  const { isAllSelected, handleShipperFieldChange, resetShipperSelection, processShipperDataForSubmit } = useShipperFieldSelection<B2BOutboundForm>(
    {
      logisticsServiceTypes: parsedQs?.logisticsServiceTypes?.length === SHIPPER_TYPES.length,
      shipperNames: String(parsedQs?.isShipperNamesSelectedAll) === 'true',
    },
  )

  const { data, expectedAllocationData, currentPage, isLoading, onChangePage, refetchAllData, setCurrentPage } = useB2BOutboundData({
    ...queryKey,
    isAllSelected,
  })

  const handleReset = useCallback(() => {
    form.reset(getB2BInitForm())
    resetShipperSelection()
    replaceQuery({ tabType: currentTabType })
  }, [form, resetShipperSelection, b2bParams, replaceQuery, currentTabType])

  const handleSubmit = useCallback(
    async (formData: B2BOutboundForm) => {
      const query = omitBy(formData, (value) => value === '') as B2BGetOrdersReq

      // 현재 탭의 search와 tabSearches를 함께 업데이트
      MenuStorage.menuOnSession.setTabSearch(currentTabType, { ...query, isShipperNamesSelectedAll: isAllSelected.shipperNames })

      flushSync(() => {
        setQueryKey({ ...query, logisticsServiceTypes: formData.logisticsServiceTypes })
        setCurrentPage(0)
      })

      reload()
      await refetchAllData()
      replaceQuery({ ...query, tabType: currentTabType, isShipperNamesSelectedAll: isAllSelected.shipperNames })
    },
    [currentTabType, reload, replaceQuery, refetchAllData, setCurrentPage, processShipperDataForSubmit],
  )

  return (
    <FormProvider {...form}>
      <B2BContent
        data={data}
        expectedAllocationData={expectedAllocationData}
        filteredWarehouses={filteredWarehouses}
        currentPage={currentPage}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        onChangePage={onChangePage}
        onReset={handleReset}
        refetchAllData={refetchAllData}
        onFieldChange={handleShipperFieldChange}
      />
    </FormProvider>
  )
}

export default B2BContentWrapper
